import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, BarChart3, PieChart, TrendingUp, Hash, Send, Clock, Sparkles } from 'lucide-react';
import { BarChart, Bar, PieChart as RPie, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const COLORS = ['#06B6D4', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899'];
const SUGGESTIONS = [
  'How many incidents were resolved this month?',
  'What is our P1 MTTR?',
  'Show me the breakdown of tickets by priority',
  'How many open tickets do we have?',
  'What percentage of incidents are auto-resolved?',
  'Compare incident volume vs changes this week',
];

export default function NlpQueryPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const token = localStorage.getItem('nexus_token');

  const { data: history } = useQuery({
    queryKey: ['query-history'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/analytics/query-history`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
  });

  const queryMutation = useMutation({
    mutationFn: async (q) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/analytics/query`, { query: q }, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: (data) => setResults(data),
  });

  const handleSubmit = (text = query) => {
    if (!text.trim()) return;
    setQuery(text);
    queryMutation.mutate(text);
  };

  return (
    <div data-testid="nlp-query-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Analytics Query</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Ask questions about your IT operations in natural language</p>
      </div>

      {/* Query input */}
      <div className="nexus-card relative overflow-hidden mb-4">
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex items-center gap-3 p-4">
          <Sparkles size={18} className="text-[var(--nexus-accent)] flex-shrink-0" />
          <input data-testid="nlp-input" type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Ask anything... e.g. 'How many P1 incidents were resolved this week?'"
            className="flex-1 bg-transparent text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none" />
          <button type="submit" disabled={queryMutation.isPending || !query.trim()} data-testid="nlp-submit"
            className="p-2 rounded-lg bg-[var(--nexus-accent)] bg-opacity-15 text-[var(--nexus-accent)] hover:bg-opacity-25 disabled:opacity-30">
            <Send size={16} />
          </button>
        </form>
      </div>

      {/* Suggestions */}
      {!results && !queryMutation.isPending && (
        <div className="flex flex-wrap gap-2 mb-4">
          {SUGGESTIONS.map(s => (
            <button key={s} onClick={() => handleSubmit(s)}
              className="px-3 py-1.5 rounded-full bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] hover:bg-white/[0.06] hover:text-[var(--nexus-accent)] transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Results */}
        <div className="lg:col-span-2">
          {queryMutation.isPending && (
            <div className="nexus-card p-8 text-center">
              <div className="flex justify-center gap-1 mb-2">
                <span className="w-2 h-2 rounded-full bg-[var(--nexus-accent)] animate-bounce" style={{animationDelay:'0ms'}} />
                <span className="w-2 h-2 rounded-full bg-[var(--nexus-accent)] animate-bounce" style={{animationDelay:'150ms'}} />
                <span className="w-2 h-2 rounded-full bg-[var(--nexus-accent)] animate-bounce" style={{animationDelay:'300ms'}} />
              </div>
              <p className="text-xs text-[var(--nexus-text-muted)]">Analyzing your question...</p>
            </div>
          )}

          {results && (
            <div className="space-y-3 animate-nexus-slide-in">
              {/* Answer */}
              <div className="nexus-card relative overflow-hidden p-4">
                <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2">Answer</h3>
                <p className="text-sm text-[var(--nexus-text-secondary)] leading-relaxed">{results.answer}</p>
                {results.insight && (
                  <div className="mt-3 p-2.5 rounded-lg bg-[var(--nexus-accent)] bg-opacity-5 border border-cyan-500/20">
                    <p className="text-[11px] text-[var(--nexus-accent)]"><strong>Insight:</strong> {results.insight}</p>
                  </div>
                )}
              </div>

              {/* Visualization */}
              {results.data?.length > 0 && (
                <div className="nexus-card relative overflow-hidden p-4">
                  <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-3">Visualization</h3>
                  {(results.visualization === 'bar' || results.visualization === 'table') && (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={results.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#475569' }} />
                        <Tooltip contentStyle={{ background: '#0F1629', border: '1px solid #1E2A45', fontSize: 11 }} />
                        <Bar dataKey="value" fill="#06B6D4" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  {results.visualization === 'pie' && (
                    <ResponsiveContainer width="100%" height={200}>
                      <RPie><Pie data={results.data} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={70} label={({name,value}) => `${name}: ${value}`}>
                        {results.data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie><Tooltip /></RPie>
                    </ResponsiveContainer>
                  )}
                  {results.visualization === 'number' && results.data[0] && (
                    <div className="text-center"><span className="font-heading text-4xl font-bold text-[var(--nexus-accent)] tabular-nums">{results.data[0].value}</span><p className="text-xs text-[var(--nexus-text-muted)] mt-1">{results.data[0].label}</p></div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Query History */}
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <div className="flex items-center gap-2"><Clock size={14} className="text-[var(--nexus-text-muted)]" /><h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Query History</h2></div>
          </div>
          <ScrollArea className="h-[400px]">
            <div className="p-2 space-y-1">
              {(history || []).map((h, i) => (
                <button key={i} onClick={() => handleSubmit(h.query)} className="w-full text-left p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.03] transition-colors">
                  <p className="text-[11px] text-[var(--nexus-text-secondary)] truncate">{h.query}</p>
                  <span className="font-mono text-[9px] text-[var(--nexus-text-muted)]">{h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}</span>
                </button>
              ))}
              {(!history || history.length === 0) && <div className="text-center py-6 text-[var(--nexus-text-muted)] text-xs">No queries yet</div>}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
